import clsx from "clsx";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "../../hooks/useTheme";
import { usePermissions } from "@/hooks/usePermissions";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { useAccess } from "../../hooks/useAccess";
import { UserMenu } from "../UserMenu";
import { MaintenanceBanner } from "../MaintenanceBanner/MaintenanceBanner";
import { ServiceUnavailableBanner } from "../ServiceUnavailableBanner/ServiceUnavailableBanner";
import { GlobalSearch } from "../GlobalSearch";
import { removeBootLoader } from "../../utils/bootLoader";
import styles from "./Layout.module.css";
import { ROUTES } from "../../routes";
import { crmWebhookLogs } from "../../api/crm-webhook-logs.service.js";
import { resetServiceAvailability } from "../../api/serviceAvailability";
import { useNavigationGuard } from "@/context/NavigationGuardContext";
import { UnsavedChangesModal } from "@/components/Modal/UnsavedChangesModal";
import { Sidebar } from "../Sidebar/Sidebar";
import { buildNavZones } from "../Sidebar/navItems";
import { useLoadTestAccountsAvailability } from "../../pages/Developer/LoadTestAccounts/availability";
import { MoveToSetRunBanner } from "../../pages/Developer/PipelineBrowser/BatchUpdates/MoveToSetRunBanner.js";
import { publicRuntimeConfig } from "../../config/publicRuntimeConfig";
import { MoveBpaRunBanner } from "../../pages/Developer/PipelineBrowser/MoveBpa/MoveBpaRunBanner.js";

type LayoutProps = {
  children: ReactNode;
  variant?: "fullscreen";
};

function Layout({ children, variant }: LayoutProps) {
  const location = useLocation();
  const rawNavigate = useNavigate();
  const { guardedNavigate, pendingNav, confirmNav, cancelNav } = useNavigationGuard();
  const navigate = useCallback(
    (path: string) => {
      guardedNavigate(() => rawNavigate(path));
    },
    [guardedNavigate, rawNavigate],
  );
  const { theme, toggleTheme } = useTheme();
  const [unhandledCrmCount, setUnhandledCrmCount] = useState(0);

  const { hasPermission, hasAnyPermission } = usePermissions();
  const { hasFlag } = useFeatureFlags();
  const { features } = useAccess();
  const isProfilesEnabled = features.profiles.enabled;

  const canSeeFulfilmentCrm = hasPermission("fulfilment.read_subscriptions");
  const isLoadTestAccountsActive =
    useLoadTestAccountsAvailability(hasAnyPermission(["platform.read"])) === "active";

  useEffect(() => {
    removeBootLoader();
  }, []);

  const previousPathname = useRef(location.pathname);
  // A layout effect so the reset lands before the new page's effects start its requests.
  useLayoutEffect(() => {
    if (previousPathname.current === location.pathname) return;
    previousPathname.current = location.pathname;
    resetServiceAvailability();
  }, [location.pathname]);

  useEffect(() => {
    if (!canSeeFulfilmentCrm) return;

    function fetchUnhandledCount() {
      crmWebhookLogs
        .getUnhandledCount()
        .then(result => setUnhandledCrmCount(result.count))
        .catch(() => {
          setUnhandledCrmCount(0);
        });
    }

    fetchUnhandledCount();
    const interval = setInterval(fetchUnhandledCount, 60000);
    return () => clearInterval(interval);
  }, [canSeeFulfilmentCrm]);

  const zones = useMemo(
    () =>
      buildNavZones({
        hasPermission,
        hasAnyPermission,
        hasFlag,
        isProfilesEnabled,
        isLoadTestAccountsActive,
        unhandledCrmCount,
      }),
    [
      hasPermission,
      hasAnyPermission,
      hasFlag,
      isProfilesEnabled,
      isLoadTestAccountsActive,
      unhandledCrmCount,
    ],
  );

  // Notifications has child routes that must all keep the parent entry highlighted.
  const activeItem = location.pathname.startsWith(ROUTES.developer.notifications)
    ? ROUTES.developer.notifications
    : location.pathname;

  return (
    <div
      className={clsx(
        styles.dashboardLayout,
        variant === "fullscreen" && styles.dashboardLayoutFullscreen,
      )}
    >
      <Sidebar
        zones={zones}
        activeItem={activeItem}
        onNavigate={navigate}
        environment={publicRuntimeConfig.environmentName}
        userMenuSlot={({ collapsed }) => (
          <UserMenu theme={theme} toggleTheme={toggleTheme} compact={collapsed} />
        )}
      />
      <div className={styles.content}>
        <header className={styles.searchBar}>
          <GlobalSearch />
        </header>
        <MaintenanceBanner />
        <ServiceUnavailableBanner />
        <MoveToSetRunBanner onNavigate={navigate} />
        <MoveBpaRunBanner onNavigate={navigate} />
        <main className={styles.main}>{children}</main>
      </div>
      {pendingNav && <UnsavedChangesModal onClose={cancelNav} onConfirm={confirmNav} />}
    </div>
  );
}

export default Layout;
